/**
 * Reduces an HTML string to text plus inline, text-level formatting elements.
 *
 * - Allowed elements are kept, with all attributes stripped (except a tiny safe list).
 * - Any other element is unwrapped (its text is kept, the tag is dropped), so wrapping
 *   HTML like <p>/<div> never reaches the DOM. This also keeps the result valid
 *   phrasing content, which is all an <output> element may contain.
 * - Elements whose content is not meant to be read as text are removed entirely.
 *
 * Angular's own [innerHTML] sanitization still runs on the result as a second layer.
 */
const ALLOWED = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'MARK',
  'SMALL',
  'SUB',
  'SUP',
  'CODE',
  'KBD',
  'SAMP',
  'VAR',
  'ABBR',
  'DFN',
  'CITE',
  'Q',
  'DEL',
  'INS',
  'TIME',
  'BR',
  'A',
  'HR'
]);
const ALLOWED_ATTRIBUTES: Readonly<Record<string, readonly string[]>> = {
  A: ['href', 'data-tooltip'],
  ABBR: ['title'],
  DFN: ['title'],
  TIME: ['datetime']
};
const DROP = new Set([
  'SCRIPT',
  'STYLE',
  'TEMPLATE',
  'NOSCRIPT',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'SELECT',
  'TEXTAREA',
  'svg',
  'math'
]);
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

export function toInlineHtml(value: string, doc: Document): string {
  // Fast path: no markup at all, nothing to parse.
  if (!value.includes('<')) {
    return value;
  }

  const template = doc.createElement('template'); // inert: nothing executes or loads
  template.innerHTML = value;
  clean(template.content);
  return template.innerHTML;
}

function clean(parent: ParentNode): void {
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === TEXT_NODE) {
      continue;
    }

    if (node.nodeType !== ELEMENT_NODE) {
      node.remove(); // comments, processing instructions, etc.
      continue;
    }

    const element = node as Element;

    if (DROP.has(element.tagName)) {
      element.remove();
      continue;
    }

    clean(element);

    if (ALLOWED.has(element.tagName)) {
      const keep = ALLOWED_ATTRIBUTES[element.tagName] ?? [];
      for (const { name } of Array.from(element.attributes)) {
        if (!keep.includes(name)) {
          element.removeAttribute(name);
        }
      }
    } else {
      element.replaceWith(...Array.from(element.childNodes));
    }
  }
}
